import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../context/UserContext';
import { csrfFetch } from '../../utils/csrfFetch';
import { reloadForNewScope } from '../../utils/reloadForNewScope';

/**
 * LocationSwitcher — Dropdown to switch active location context (DOS-70)
 *
 * Options:
 *   - "Group View" (null active_location_id)
 *   - Each of the user's assigned locations
 *
 * On selection: calls PUT /api/me/active-location, then reloads so the
 * new session context propagates to all subsequent chat requests.
 *
 * Hidden when the user has no locations assigned (group-only users).
 *
 * The menu opens upward and is rendered in a portal with fixed positioning,
 * sized to the space above the button and scrollable. Rendered in place, it
 * was clipped by the sidebar's scrolling Settings & Admin panel and had no max
 * height — with a 28-location dealer the top of the list was unreachable.
 */
function LocationSwitcher() {
  const { org, loading: authLoading } = useUser();
  const [open, setOpen]             = useState(false);
  const [locations, setLocations]   = useState([]);
  const [activeId, setActiveId]     = useState(null); // null = Group View
  const [switching, setSwitching]   = useState(false);
  const [loaded, setLoaded]         = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  // Close on outside click (the menu lives in a portal, so check it too)
  // or on Escape
  useEffect(() => {
    function handleClick(e) {
      const inside = ref.current?.contains(e.target) || menuRef.current?.contains(e.target);
      if (!inside) setOpen(false);
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  // Load user's assigned locations + current active location
  const load = useCallback(() => {
    if (!org?.id || loaded) return;
    Promise.all([
      csrfFetch('/api/me/locations', { credentials: 'include' }).then(r => r.json()),
      csrfFetch('/api/me/active-location', { credentials: 'include' }).then(r => r.json()),
    ]).then(([locData, activeData]) => {
      setLocations(locData.locations || []);
      setActiveId(activeData.location?.id || null);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [org?.id, loaded]);

  useEffect(() => { load(); }, [load]);

  // Position the portal menu just above the button, using the space above it.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    function place() {
      const r = buttonRef.current.getBoundingClientRect();
      const gap = 4;
      setMenuStyle({
        position: 'fixed',
        left: r.left,
        width: r.width,
        bottom: window.innerHeight - r.top + gap,
        maxHeight: Math.max(160, r.top - 2 * gap),
      });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Hide if no locations are assigned
  if (authLoading || !loaded || locations.length === 0) return null;

  const activeName = activeId
    ? (locations.find(l => l.id === activeId)?.name || 'Location')
    : 'Group View';

  async function handleSwitch(locationId) {
    if (locationId === activeId) { setOpen(false); return; }
    setSwitching(true);
    try {
      const res = await csrfFetch('/api/me/active-location', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location_id: locationId ?? null }),  // the server reads location_id
      });
      if (res.ok) {
        setActiveId(locationId);
        setOpen(false);
        // Load fresh for the new location (an open chat goes home)
        reloadForNewScope();
      }
    } catch { /* ignore */ } finally {
      setSwitching(false);
    }
  }

  return (
    <div ref={ref} data-testid="location-switcher" className="relative">
      <button
        ref={buttonRef}
        data-testid="location-switcher-button"
        onClick={() => setOpen(!open)}
        disabled={switching}
        title="Switch active location context"
        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors
          text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
      >
        {/* Location pin icon */}
        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="flex-1 text-left truncate">{activeName}</span>
        <svg className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          data-testid="location-switcher-menu"
          role="listbox"
          style={menuStyle || { position: 'fixed', visibility: 'hidden' }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
            rounded-lg shadow-lg z-50 overflow-y-auto">

          {/* Group View option */}
          <button
            data-testid="location-option-null"
            onClick={() => handleSwitch(null)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors
              ${activeId === null
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
          >
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
            <span className="flex-1 truncate">Group View</span>
            {activeId === null && (
              <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          {/* Divider */}
          <div className="border-t border-gray-100 dark:border-gray-700 my-0.5" />

          {/* Location options */}
          {locations.map(loc => (
            <button
              key={loc.id}
              data-testid={`location-option-${loc.id}`}
              onClick={() => handleSwitch(loc.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors
                ${loc.id === activeId
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
            >
              <svg className="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <div className="flex-1 min-w-0">
                <div className="truncate">{loc.name}</div>
                {(loc.oem || loc.dms) && (
                  <div className="text-xs text-gray-400 truncate">
                    {[loc.oem, loc.dms].filter(Boolean).join(' / ')}
                  </div>
                )}
              </div>
              {loc.id === activeId && (
                <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default LocationSwitcher;
